/**
 * Service de masquage des données personnelles identifiables (PII)
 * Utilisé pour anonymiser les données avant envoi à Sentry
 *
 * Contexte: Application e-learning (élèves, enseignants, parents)
 */
export class PiiMaskingService {
  // Patterns PII basiques
  private readonly emailRegex = /(\b[A-Za-z0-9._%+-]+)@([A-Za-z0-9.-]+\.[A-Z|a-z]{2,})/gi
  private readonly phoneRegex = /(\+33|0)[1-9][\d\s.]{8,12}/gi
  private readonly tokenRegex =
    /(token|password|secret|bearer|authorization|auth|api[_-]?key)[\s:=]+([A-Za-z0-9._\-+=]{8,})/gi
  private readonly ipRegex = /\b\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}\b/g
  private readonly birthdateRegex =
    /(birth[\s_-]?date|date[_-]?of[_-]?birth|dob)(["'\s:=]+)(\d{4}[-/]\d{2}[-/]\d{2})/gi

  private readonly firstnameRegex =
    /(firstname|first[_-]?name|prenom|prénom)(["'\s:=]+)([A-Za-zÀ-ÿ]{2,})/gi
  private readonly lastnameRegex =
    /(lastname|last[_-]?name|nom|surname)(["'\s:=]+)([A-Za-zÀ-ÿ]{2,})/gi
  private readonly usernameRegex = /(username|user[_-]?name|login)(["'\s:=]+)([A-Za-z0-9._-]{2,})/gi
  private readonly genderRegex = /(gender|sexe|sex)(["'\s:=]+)(male|female|homme|femme|m|f|h)/gi

  /**
   * Masque les PII dans un message texte.
   * Utilisé par SentryConfig pour les extras.
   */
  public maskMessage(message: string): string {
    if (!message || message.trim().length === 0) {
      return message
    }

    return message
      .replace(this.emailRegex, '***@$2')
      .replace(this.phoneRegex, '[REDACTED]')
      .replace(this.tokenRegex, '$1=[REDACTED]')
      .replace(this.ipRegex, '***.***.***.***')
      .replace(this.birthdateRegex, '$1$2****-**-**')
      .replace(this.firstnameRegex, (match, g1, g2, g3) => `${g1}${g2}${this.maskName(g3)}`)
      .replace(this.lastnameRegex, (match, g1, g2, g3) => `${g1}${g2}${this.maskName(g3)}`)
      .replace(this.usernameRegex, (match, g1, g2, g3) => `${g1}${g2}${this.maskName(g3)}`)
      .replace(this.genderRegex, '$1$2[REDACTED]')
  }

  /**
   * Masque un nom en conservant uniquement le premier caractère.
   */
  private maskName(name: string): string {
    return `${name.charAt(0)}***`
  }

  /**
   * Masque récursivement les PII dans un objet
   * Utilisé pour nettoyer les extras/context Sentry
   */
  public maskObject(obj: any): any {
    if (obj === null || obj === undefined) {
      return obj
    }

    // Si c'est une chaîne, masquer directement
    if (typeof obj === 'string') {
      return this.maskMessage(obj)
    }

    // Si c'est un tableau, mapper récursivement
    if (Array.isArray(obj)) {
      return obj.map((item) => this.maskObject(item))
    }

    // Si c'est un objet, traiter chaque propriété
    if (typeof obj === 'object') {
      const masked: Record<string, any> = {}

      for (const [key, value] of Object.entries(obj)) {
        const lowerKey = key.toLowerCase()

        // Clés sensibles : masquer complètement la valeur
        if (this.isSensitiveKey(lowerKey)) {
          masked[key] = '[REDACTED]'
          continue
        }

        // Valeur non sensible : masquer récursivement les patterns PII
        masked[key] = this.maskObject(value)
      }

      return masked
    }

    // Types primitifs : retourner tel quel
    return obj
  }

  /**
   * Vérifie si une clé est considérée comme sensible
   */
  private isSensitiveKey(key: string): boolean {
    const sensitiveKeys = [
      'password',
      'passwd',
      'pwd',
      'secret',
      'token',
      'bearer',
      'authorization',
      'auth',
      'api_key',
      'apikey',
      'private_key',
      'privatekey',
      'access_token',
      'refresh_token',
      'session_id',
      'sessionid',
      'cookie',
      'birthdate',
      'birth_date',
      'date_of_birth',
      'dob',
      'firstname',
      'first_name',
      'prenom',
      'lastname',
      'last_name',
      'nom',
      'surname',
      'username',
      'user_name',
      'login',
      'gender',
      'sexe'
    ]

    return sensitiveKeys.some((sensitive) => key.includes(sensitive))
  }

  /**
   * Masque les données d'erreur pour Sentry
   * Combine le masquage de message et d'objet
   */
  public maskErrorData(error: any): any {
    if (!error) return error

    const masked: any = {}

    // Message d'erreur
    if (error.message && typeof error.message === 'string') {
      masked.message = this.maskMessage(error.message)
    }

    // Stack trace
    if (error.stack && typeof error.stack === 'string') {
      masked.stack = this.maskMessage(error.stack)
    }

    // Autres propriétés
    for (const [key, value] of Object.entries(error)) {
      if (key === 'message' || key === 'stack') {
        continue // Déjà traité
      }

      masked[key] = this.maskObject(value)
    }

    return masked
  }
}

// Singleton pour éviter de recréer les regex à chaque appel
export const piiMaskingService = new PiiMaskingService()
